/**
 * VariantOverridesDisplay Component
 *
 * Read-only two-section display for existing KPI Variant driver dimensions.
 * Section 1: Override dimensions (always visible) — values from variant_overrides.
 * Section 2: Inherited dimensions (collapsible, collapsed by default) — values from page drivers.
 *
 * Clicking any field is intended to open the KPI Library dialog for editing the variant.
 */

import './VariantOverridesDisplay.scss';

import { KPI_DIMENSION_RENDER_LIST } from '@constants/page';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { useCallback, useMemo } from 'react';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { KpiDimensionConfig } from 'types/PageSidePanel.types';

export interface VariantOverridesDisplayProps {
  /** The full driver display options (merged page + overrides) for the deckline */
  driverOptions: DriverDisplayOptionMap;
  /** The variant_overrides from the existing KPIVariant */
  variantOverrides: KPIDrivers | null;
  /** Whether the inherited dimensions section is expanded */
  showInherited: boolean;
  /** Callback to toggle the inherited dimensions section */
  onToggleInherited: () => void;
  /** Callback when user clicks a dimension row (intended to open KPI Library dialog) */
  onFieldClick?: () => void;
  /** Whether the component is in a disabled/loading state */
  disabled?: boolean;
}

const BASE_CLASS = 'variant-overrides-display';

/**
 * Determines if a dimension has an override value in the variant_overrides object.
 */
const hasOverride = (config: KpiDimensionConfig, overrides: KPIDrivers | null): boolean => {
  if (!overrides || !config.decklineDrivers) return false;
  const value = overrides[config.decklineDrivers.valueAccessor];
  return value !== null && value !== undefined;
};

const VariantOverridesDisplay = ({
  driverOptions,
  variantOverrides,
  showInherited,
  onToggleInherited,
  onFieldClick,
  disabled = false,
}: VariantOverridesDisplayProps) => {
  /** Split dimensions into overrides vs inherited based on variant_overrides presence and resolved value */
  const { overrideDimensions, inheritedDimensions } = useMemo(() => {
    return KPI_DIMENSION_RENDER_LIST.filter((config) => !!driverOptions[config.key]).reduce<{
      overrideDimensions: KpiDimensionConfig[];
      inheritedDimensions: KpiDimensionConfig[];
    }>(
      (acc, config) => {
        const entry = driverOptions[config.key];
        const hasValue = !!entry?.value;
        if (hasOverride(config, variantOverrides) || !hasValue) {
          acc.overrideDimensions.push(config);
        } else {
          acc.inheritedDimensions.push(config);
        }
        return acc;
      },
      { overrideDimensions: [], inheritedDimensions: [] },
    );
  }, [driverOptions, variantOverrides]);

  const renderRow = useCallback(
    (config: KpiDimensionConfig) => {
      const entry = driverOptions[config.key];
      if (!entry) return null;
      const isUnresolved = !entry.value && !hasOverride(config, variantOverrides);
      return (
        <TextField
          key={config.key}
          label={config.label}
          value={entry.value || ''}
          size='small'
          fullWidth
          error={isUnresolved}
          helperText={isUnresolved ? 'No value assigned' : undefined}
          slotProps={{ input: { readOnly: true } }}
          disabled={disabled}
          className={`${BASE_CLASS}__row`}
          // Opens KPI Library dialog to the variant edit view
          onClick={onFieldClick}
        />
      );
    },
    [driverOptions, variantOverrides, disabled, onFieldClick],
  );

  return (
    <Box className={BASE_CLASS}>
      <Stack spacing={1.5} className={`${BASE_CLASS}__overrides`}>
        {overrideDimensions.map(renderRow)}
      </Stack>

      {inheritedDimensions.length > 0 && (
        <>
          <Button variant='text' size='small' className={`${BASE_CLASS}__toggle`} onClick={onToggleInherited}>
            {showInherited ? 'Hide Inherited Dimensions' : 'Show Inherited Dimensions'}
          </Button>
          <Collapse in={showInherited}>
            <Stack spacing={1.5} className={`${BASE_CLASS}__inherited`}>
              {inheritedDimensions.map(renderRow)}
            </Stack>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default VariantOverridesDisplay;
